import { useState, useEffect } from "react";
import { Film, FileText, BookOpen, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useNavigate } from "react-router-dom";

export default function AdminContent() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/courses"),
      api.get("/analytics/content-stats"),
    ]).then(([coursesRes, statsRes]) => {
      setCourses(coursesRes.data);
      setStats(statsRes.data);
    }).catch((err) => console.error(err)).finally(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout><div className="admin-text-secondary py-12 text-center">Loading...</div></AdminLayout>;

  const videoCourses = courses.filter(c => c.content_type === "course");
  const books = courses.filter(c => c.content_type === "book");

  return (
    <AdminLayout>
      <div data-testid="admin-content-page">
        <div className="mb-6">
          <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Content Management
          </h1>
          <p className="text-sm admin-text-secondary mt-0.5">Videos, PDFs, Notes & Course Materials</p>
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="admin-card border p-4 flex items-center gap-3">
            <Film size={18} className="text-[#0284C7]" />
            <div>
              <p className="text-xl font-black admin-text">{stats?.total_videos || 0}</p>
              <p className="text-[10px] admin-text-secondary">Total Videos</p>
            </div>
          </div>
          <div className="admin-card border p-4 flex items-center gap-3">
            <BookOpen size={18} className="text-[#7C3AED]" />
            <div>
              <p className="text-xl font-black admin-text">{stats?.total_chapters || 0}</p>
              <p className="text-[10px] admin-text-secondary">Total Chapters</p>
            </div>
          </div>
          <div className="admin-card border p-4 flex items-center gap-3">
            <Film size={18} className="text-[#059669]" />
            <div>
              <p className="text-xl font-black admin-text">{stats?.courses_by_type?.course || 0}</p>
              <p className="text-[10px] admin-text-secondary">Video Courses</p>
            </div>
          </div>
          <div className="admin-card border p-4 flex items-center gap-3">
            <FileText size={18} className="text-[#D97706]" />
            <div>
              <p className="text-xl font-black admin-text">{stats?.courses_by_type?.book || 0}</p>
              <p className="text-[10px] admin-text-secondary">Books / PDFs</p>
            </div>
          </div>
        </div>

        {/* Video Courses */}
        <div className="admin-card border mb-4">
          <div className="px-5 py-4 border-b admin-table-row flex items-center justify-between">
            <h2 className="text-base font-bold admin-text flex items-center gap-2">
              <Film size={16} className="text-[#0284C7]" /> Video Courses
            </h2>
            <button onClick={() => navigate("/admin/courses")} className="text-xs font-bold text-[#0284C7] flex items-center gap-1">
              Manage All <ChevronRight size={12} />
            </button>
          </div>
          <div className="divide-y admin-table-row">
            {videoCourses.map((course) => (
              <div key={course.id} className="px-5 py-3 flex items-center gap-3 admin-table-row">
                <div className="w-16 aspect-video bg-slate-200 flex-shrink-0 overflow-hidden">
                  <img src={course.image_url || ""} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium admin-text truncate">{course.title}</p>
                  <p className="text-[10px] admin-text-secondary">{course.category}</p>
                </div>
                <button
                  onClick={() => navigate(`/admin/courses/${course.id}/chapters`)}
                  className="text-xs font-bold text-[#0284C7] px-3 py-1.5 bg-[#0284C7]/10 transition-none"
                >
                  Chapters
                </button>
              </div>
            ))}
            {videoCourses.length === 0 && <p className="px-5 py-6 text-sm admin-text-secondary text-center">No video courses</p>}
          </div>
        </div>

        {/* Books / PDFs */}
        <div className="admin-card border">
          <div className="px-5 py-4 border-b admin-table-row">
            <h2 className="text-base font-bold admin-text flex items-center gap-2">
              <FileText size={16} className="text-[#D97706]" /> Books & PDFs
            </h2>
          </div>
          <div className="divide-y admin-table-row">
            {books.map((book) => (
              <div key={book.id} className="px-5 py-3 flex items-center gap-3 admin-table-row">
                <div className="w-16 aspect-video bg-slate-200 flex-shrink-0 overflow-hidden">
                  <img src={book.image_url || ""} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium admin-text truncate">{book.title}</p>
                  <p className="text-[10px] admin-text-secondary">₹{book.price}</p>
                </div>
                <button
                  onClick={() => navigate(`/admin/courses`)}
                  className="text-xs font-bold admin-text-secondary px-3 py-1.5 admin-card border transition-none"
                >
                  Edit
                </button>
              </div>
            ))}
            {books.length === 0 && <p className="px-5 py-6 text-sm admin-text-secondary text-center">No books/PDFs</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
